// /app/frontend/src/pages/settings/NotificationPreferencesPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { 
  Bell, AlertTriangle, FileText, CheckSquare, 
  Mail, Save, Loader2, ArrowLeft, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Notification categories with their settings
const NOTIFICATION_CATEGORIES = [
  {
    id: 'breaches',
    title: 'Data Breach Notifications',
    icon: AlertTriangle,
    color: 'text-red-500',
    description: 'Notifications about data breach incidents',
    settings: [
      { key: 'breach_new', label: 'New breach reported', description: 'When a new breach is created or reported' },
      { key: 'breach_status_change', label: 'Breach status changes', description: 'When breach status is updated' },
      { key: 'breach_deadline_reminder', label: 'Breach deadline reminders', description: '72-hour reporting deadline alerts' },
    ]
  },
  {
    id: 'dsrs',
    title: 'Data Subject Request Notifications',
    icon: FileText,
    color: 'text-blue-500',
    description: 'Notifications about DSR processing',
    settings: [
      { key: 'dsr_new', label: 'New DSR received', description: 'When a new request is submitted' },
      { key: 'dsr_assigned', label: 'DSR assigned to you', description: 'When you are assigned a request' },
      { key: 'dsr_deadline_reminder', label: 'DSR deadline reminders', description: '30-day response deadline alerts' },
    ]
  },
  {
    id: 'tasks',
    title: 'Task Notifications',
    icon: CheckSquare,
    color: 'text-amber-500',
    description: 'Notifications about assigned tasks',
    settings: [
      { key: 'task_assigned', label: 'Task assigned to you', description: 'When you are assigned a new task' },
      { key: 'task_deadline_reminder', label: 'Task deadline reminders', description: 'Upcoming task due date alerts' },
      { key: 'task_completed', label: 'Task completion updates', description: 'When tasks you created are completed' },
    ]
  },
  {
    id: 'digest',
    title: 'Summary Digest',
    icon: Mail,
    color: 'text-teal-500',
    description: 'Periodic summary emails',
    settings: [
      { key: 'weekly_digest', label: 'Weekly digest', description: 'Weekly summary of all compliance activities' },
    ]
  },
];

export default function NotificationPreferencesPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [preferences, setPreferences] = useState({
    breach_new: true,
    breach_status_change: true,
    breach_deadline_reminder: true,
    dsr_new: true,
    dsr_assigned: true,
    dsr_deadline_reminder: true,
    task_assigned: true,
    task_deadline_reminder: true,
    task_completed: true,
    weekly_digest: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPrefs, setOriginalPrefs] = useState(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me/notification-preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
        setOriginalPrefs(data.preferences);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    const newPrefs = { ...preferences, [key]: !preferences[key] };
    setPreferences(newPrefs);
    setHasChanges(JSON.stringify(newPrefs) !== JSON.stringify(originalPrefs));
  };

  const handleToggleCategory = (categoryId, enabled) => {
    const category = NOTIFICATION_CATEGORIES.find(c => c.id === categoryId);
    if (!category) return;

    const newPrefs = { ...preferences };
    category.settings.forEach(setting => {
      newPrefs[setting.key] = enabled;
    });
    setPreferences(newPrefs);
    setHasChanges(JSON.stringify(newPrefs) !== JSON.stringify(originalPrefs));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/auth/me/notification-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(preferences)
      });

      if (response.ok) {
        toast.success('Notification preferences saved');
        setOriginalPrefs(preferences);
        setHasChanges(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleEnableAll = () => {
    const newPrefs = {};
    NOTIFICATION_CATEGORIES.forEach(category => {
      category.settings.forEach(setting => {
        newPrefs[setting.key] = true;
      });
    });
    setPreferences(newPrefs);
    setHasChanges(JSON.stringify(newPrefs) !== JSON.stringify(originalPrefs));
  };

  const handleDisableAll = () => {
    const newPrefs = {};
    NOTIFICATION_CATEGORIES.forEach(category => {
      category.settings.forEach(setting => {
        newPrefs[setting.key] = false;
      });
    });
    setPreferences(newPrefs);
    setHasChanges(JSON.stringify(newPrefs) !== JSON.stringify(originalPrefs));
  };

  const getCategoryStatus = (categoryId) => {
    const category = NOTIFICATION_CATEGORIES.find(c => c.id === categoryId);
    if (!category) return { enabled: 0, total: 0 };
    
    const enabled = category.settings.filter(s => preferences[s.key]).length;
    return { enabled, total: category.settings.length };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Bell className="h-6 w-6" />
                Notification Preferences
              </h1>
              <p className="text-muted-foreground">
                Configure how and when you receive email notifications
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Badge variant="secondary" className="animate-pulse">
                Unsaved changes
              </Badge>
            )}
            <Button onClick={handleSave} disabled={saving || !hasChanges}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Quick Actions</p>
                <p className="text-sm text-muted-foreground">Quickly enable or disable all notifications</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleEnableAll}>
                  Enable All
                </Button>
                <Button variant="outline" size="sm" onClick={handleDisableAll}>
                  Disable All
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification Categories */}
        <div className="space-y-6">
          {NOTIFICATION_CATEGORIES.map((category) => {
            const status = getCategoryStatus(category.id);
            const Icon = category.icon;
            const allEnabled = status.enabled === status.total;
            
            return (
              <Card key={category.id} data-testid={`notification-category-${category.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-muted ${category.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{category.title}</CardTitle>
                        <CardDescription>{category.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={allEnabled ? "default" : "secondary"}>
                        {status.enabled}/{status.total} enabled
                      </Badge>
                      <Switch
                        checked={allEnabled}
                        onCheckedChange={(checked) => handleToggleCategory(category.id, checked)}
                        data-testid={`toggle-category-${category.id}`}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {category.settings.map((setting, idx) => (
                      <div key={setting.key}>
                        {idx > 0 && <Separator className="my-3" />}
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor={setting.key} className="text-sm font-medium cursor-pointer">
                              {setting.label}
                            </Label>
                            <p className="text-xs text-muted-foreground">{setting.description}</p>
                          </div>
                          <Switch
                            id={setting.key}
                            checked={preferences[setting.key]}
                            onCheckedChange={() => handleToggle(setting.key)}
                            data-testid={`toggle-${setting.key}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Card */}
        <Card className="mt-6 bg-muted/50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">About Email Notifications</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Email notifications are sent to <strong>{user?.email}</strong>. 
                  Deadline reminders are typically sent 24-48 hours before the deadline. 
                  Weekly digests are sent every Monday morning.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
